/* ==================================================================
   VS | Collection Circles — JavaScript
   Vanilla JS, no dependencies
   Handles: arrow scrolling, edge fade state, keyboard support,
            scroll-into-view reveal animation
================================================================== */

(function () {
  'use strict';

  function VsCollectionCircles(section) {
    this.section = section;
    this.wrapper = section.querySelector('.vs-cc__row-wrapper');
    this.track = section.querySelector('[data-vs-track]');
    this.prevBtn = section.querySelector('[data-vs-arrow="prev"]');
    this.nextBtn = section.querySelector('[data-vs-arrow="next"]');
    this.autoHide = section.getAttribute('data-auto-hide-arrows') === 'true';
    this.animation = section.getAttribute('data-animation') || 'none';

    this.reducedMotion = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

    if (!this.track) {
      return;
    }

    this.bindArrows();
    this.bindScrollState();
    this.bindKeyboard();
    this.observeReveal();

    // Set initial state once layout settles.
    window.requestAnimationFrame(this.updateScrollState.bind(this));
  }

  /* ----------------------------------------------------------------
     Arrow click handlers — scroll by one viewport-ish page
  ---------------------------------------------------------------- */
  VsCollectionCircles.prototype.getScrollAmount = function () {
    var item = this.track.querySelector('.vs-cc__item');
    var itemWidth = item ? item.getBoundingClientRect().width : 120;
    var visibleWidth = this.track.clientWidth;
    var itemsPerPage = Math.max(1, Math.floor(visibleWidth / (itemWidth + 24)));
    return itemsPerPage * (itemWidth + 24);
  };

  VsCollectionCircles.prototype.bindArrows = function () {
    var self = this;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.track.scrollBy({
          left: -self.getScrollAmount(),
          behavior: self.reducedMotion ? 'auto' : 'smooth'
        });
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.track.scrollBy({
          left: self.getScrollAmount(),
          behavior: self.reducedMotion ? 'auto' : 'smooth'
        });
      });
    }
  };

  /* ----------------------------------------------------------------
     Keyboard support when the track itself is focused
  ---------------------------------------------------------------- */
  VsCollectionCircles.prototype.bindKeyboard = function () {
    var self = this;

    this.track.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        self.track.scrollBy({ left: self.getScrollAmount(), behavior: 'smooth' });
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        self.track.scrollBy({ left: -self.getScrollAmount(), behavior: 'smooth' });
      }
    });
  };

  /* ----------------------------------------------------------------
     Track scroll state — fade edges + disable/hide arrows at ends
  ---------------------------------------------------------------- */
  VsCollectionCircles.prototype.bindScrollState = function () {
    var self = this;
    var ticking = false;

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          self.updateScrollState();
          ticking = false;
        });
        ticking = true;
      }
    }

    this.track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  };

  VsCollectionCircles.prototype.updateScrollState = function () {
    if (!this.track || !this.wrapper) {
      return;
    }

    var scrollLeft = this.track.scrollLeft;
    var maxScroll = this.track.scrollWidth - this.track.clientWidth;
    var atStart = scrollLeft <= 4;
    var atEnd = maxScroll <= 4 || scrollLeft >= maxScroll - 4;

    this.wrapper.setAttribute('data-vs-scrolled', atStart ? 'false' : 'true');
    this.wrapper.setAttribute('data-vs-at-end', atEnd ? 'true' : 'false');

    if (this.autoHide) {
      if (this.prevBtn) {
        this.prevBtn.setAttribute('data-vs-hidden', atStart ? 'true' : 'false');
        this.prevBtn.disabled = atStart;
      }
      if (this.nextBtn) {
        this.nextBtn.setAttribute('data-vs-hidden', atEnd ? 'true' : 'false');
        this.nextBtn.disabled = atEnd;
      }
    }

    // If there's nothing to scroll at all, hide both arrows.
    if (maxScroll <= 4) {
      if (this.prevBtn) this.prevBtn.setAttribute('data-vs-hidden', 'true');
      if (this.nextBtn) this.nextBtn.setAttribute('data-vs-hidden', 'true');
    }
  };

  /* ----------------------------------------------------------------
     Reveal animation when the section scrolls into view
  ---------------------------------------------------------------- */
  VsCollectionCircles.prototype.observeReveal = function () {
    var self = this;

    if (this.animation === 'none' || this.reducedMotion) {
      this.section.classList.add('vs-is-visible');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      this.section.classList.add('vs-is-visible');
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            self.section.classList.add('vs-is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { root: null, threshold: 0.15 }
    );

    observer.observe(this.section);
  };

  /* ----------------------------------------------------------------
     Instance registry + initialization (theme editor aware)
  ---------------------------------------------------------------- */
  var instances = new WeakMap();

  function initSection(section) {
    if (!section || instances.has(section)) {
      return;
    }
    instances.set(section, new VsCollectionCircles(section));
  }

  function initAll(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll('[data-vs-collection-circles]');
    sections.forEach(initSection);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-vs-collection-circles]');
    if (section) {
      initSection(section);
    }
  });
})();
